import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import type React from "react";

import { useScopeClass } from "@/components/ui/scope";
import { cn } from "@/lib/utils";

function injectRipple(e: React.MouseEvent<HTMLButtonElement>) {
  const btn = e.currentTarget;
  const rect = btn.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height) * 2;
  const el = document.createElement("span");
  el.className = "bday-ripple-el";
  el.style.cssText = `width:${size}px;height:${size}px;left:${e.clientX - rect.left - size / 2}px;top:${e.clientY - rect.top - size / 2}px`;
  btn.appendChild(el);
  el.addEventListener("animationend", () => el.remove(), { once: true });
  // animationend never fires when animations are disabled (prefers-reduced-motion,
  // test stylesheets) — reap the span regardless so they can't accumulate.
  window.setTimeout(() => el.remove(), 700);
}

const buttonVariants = cva(
  // MUI parity: transition background-color/box-shadow/border-color/color over
  // 0.25s cubic-bezier(0.4,0,0.2,1) (measured on the old app). Per-scope press/
  // hover treatment (public MUI-darken+ripple vs admin elevate) is applied below.
  "group/button inline-flex shrink-0 items-center justify-center gap-1 rounded-md border border-transparent bg-clip-padding text-base font-medium leading-[1.75] whitespace-nowrap outline-none transition-[background-color,box-shadow,border-color,color] duration-250 ease-[cubic-bezier(0.4,0,0.2,1)] select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground" /* border stays transparent (MUI) */,
        orange: "bg-brand-orange text-white border-brand-orange",
        destructive:
          "bg-destructive-solid text-destructive-foreground border border-destructive-solid-border",
        outline: " border border-(--button-outline)  shadow-xs active:shadow-none ",
        secondary: "border bg-secondary text-secondary-foreground border border-secondary-border ",
        ghost: "border border-transparent",
      },
      // Heights are set as "min" heights, because sometimes Ai will place large amount of content
      // inside buttons. With a min-height they will look appropriate with small amounts of content,
      // but will expand to fit large amounts of content.
      size: {
        default: "min-h-9 px-4 py-[0.4rem]" /* MUI contained: padding 0.4rem 1rem */,
        sm: "min-h-8 rounded-md px-3 text-sm",
        lg: "min-h-10 rounded-md px-8",
        /* Old MUI primary page CTA (ADD REMINDER / NEW CONTACT / MY HOLIDAYS):
           49px pill, wide padding, spaced uppercase. */
        cta: "min-h-[49px] px-[40px] py-[0.4rem] tracking-[2px] uppercase",
        /* Old MUI small contained button (landing-page CTAs): 39px, 12px text. */
        compact: "min-h-[39px] px-6 py-2 text-[0.75rem] tracking-[1.5px]",
        icon: "size-9",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),8px)] [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 rounded-[min(var(--radius-md),10px)]",
        "icon-lg": "size-10",
      },
      shape: {
        default: "",
        pill: "rounded-[2rem]" /* matches MUI button radius (2rem / 32px) */,
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  shape = "pill",
  onMouseDown,
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  // Admin keeps the original (pre-brand-restyle) button look: original padding +
  // leading, and the default variant's primary border. The public site keeps the
  // new MUI-style contained button. (Radius is handled by the `.admin-scope
  // [data-slot="button"]` rule in index.css.)
  const isAdmin = useScopeClass() === "admin-scope";

  // Press / hover feedback is scope-specific:
  // - Admin (Nick's lane): the generic elevate overlay system — unchanged.
  // - Public (Joseph's lane): match the old MUI contained button — hover darkens
  //   the fill to palette.primary.dark (#11775e, measured live) and press shows a
  //   white ripple originating from the exact click point (matching MUI's
  //   MuiTouchRipple), with no elevation. Non-solid public variants keep the
  //   elevate overlay, which reads correctly on their light/transparent fills.
  const isSolid = variant === "default" || variant === "orange" || variant === "destructive";
  const publicSolidHover =
    variant === "default"
      ? "hover:bg-primary-dark" // #11775e — the measured MUI primary.dark hover
      : variant === "orange"
        ? "hover:bg-brand-orange-dark"
        : "hover:bg-destructive-dark";

  return (
    <ButtonPrimitive
      data-slot="button"
      onMouseDown={(e) => {
        onMouseDown?.(e);
        if (!isAdmin && isSolid) injectRipple(e);
      }}
      className={cn(
        buttonVariants({ variant, size, shape }),
        isAdmin && "hover-elevate active-elevate-2",
        isAdmin && "py-2 leading-normal",
        isAdmin && variant === "default" && "border-primary",
        !isAdmin && isSolid && cn("public-button-ripple", publicSolidHover),
        !isAdmin && !isSolid && "hover-elevate active-elevate-2",
        className,
      )}
      {...props}
    />
  );
}

export { Button, buttonVariants };
